import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ShortLinkModal from './ShortLinkModal';
import AnalyticsModal from './AnalyticsModal';
import { shortLinkService } from '../../../utils/storage';
import { useToast } from '../../../hooks/useToast';

export default function ShortLinkList() {
  const { t } = useTranslation('dashboard');
  const { showToast, ToastContainer } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [analyticsModal, setAnalyticsModal] = useState<{ isOpen: boolean; link: ShortLink | null }>({
    isOpen: false,
    link: null,
  });
  const [links, setLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadLinks = async () => {
    try {
      setLoading(true);
      const data = await shortLinkService.getAll();
      setLinks(data);
    } catch (error) {
      console.error('링크 로드 실패:', error);
      showToast('링크를 불러오는데 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      await shortLinkService.delete(id);
      showToast('링크가 삭제되었습니다', 'success');
      loadLinks();
    } catch (error) {
      console.error('링크 삭제 실패:', error);
      showToast('링크 삭제에 실패했습니다', 'error');
    }
  };

  const handleCopy = (shortCode: string) => {
    const url = `${window.location.origin}/s/${shortCode}`;
    navigator.clipboard.writeText(url);
    showToast('링크가 복사되었습니다', 'success');
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
        <p className="text-gray-600 mt-4">로딩 중...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">짧은 링크 목록</h3>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i>
              {t('link.create')}
            </button>
          </div>
        </div>

        {links.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-link text-6xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-500 mb-4">아직 생성된 짧은 링크가 없습니다</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              첫 짧은 링크 만들기
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link) => (
              <div key={link.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{link.name}</h4>
                    <p className="text-sm text-gray-600 truncate mb-2">{link.original_url}</p>
                    <div className="flex items-center gap-4">
                      <code className="px-3 py-1 bg-teal-50 text-teal-700 rounded text-sm font-mono">
                        {window.location.origin}/s/{link.short_code}
                      </code>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-cursor-line"></i>
                          {link.clicks || 0} 클릭
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {new Date(link.created_at).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAnalyticsModal({ isOpen: true, link })}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-bar-chart-line mr-2"></i>
                      분석
                    </button>
                    <button
                      onClick={() => handleCopy(link.short_code)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      title="복사"
                    >
                      <i className="ri-file-copy-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(link.id)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                      title="삭제"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <ShortLinkModal
          onClose={() => setShowModal(false)}
          onSuccess={loadLinks}
        />
      )}

      {analyticsModal.link && (
        <AnalyticsModal
          isOpen={analyticsModal.isOpen}
          onClose={() => setAnalyticsModal({ isOpen: false, link: null })}
          itemId={analyticsModal.link.id}
          itemName={analyticsModal.link.name}
          type="link"
        />
      )}
    </>
  );
}
